import { BookOpen, Search, ArrowUpRight } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { useApi } from '../api/api-context';
import type { Repertoire } from '../api/types';
import { buildNodeRoute } from '../state/route';
import { recentBuilderRoute } from '../state/recent-builder';
import { GameLinkPrompt } from './GameLinkPrompt';
import { DueBadge } from './PracticeSummary';

const STARTING_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

interface ConfirmDeleteModalProps {
  repertoire: Repertoire;
  busy: boolean;
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

/** Accessible confirm modal for deleting a repertoire (§4 DELETE). */
function ConfirmDeleteModal({
  repertoire,
  busy,
  error,
  onConfirm,
  onCancel,
}: ConfirmDeleteModalProps): JSX.Element {
  const cancelRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    cancelRef.current?.focus();
    function onKeyDown(event: globalThis.KeyboardEvent): void {
      if (event.key === 'Escape' && !busy) onCancel();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onCancel, busy]);

  return (
    <div className="modal-backdrop" data-testid="confirm-delete">
      <div role="dialog" aria-modal="true" aria-labelledby="confirm-delete-title" className="modal">
        <h2 id="confirm-delete-title">Delete “{repertoire.name}”?</h2>
        <p className="muted">
          This schedules the repertoire and its lines for deletion. It cannot be undone.
        </p>
        {error !== null && (
          <p role="alert" className="error">
            {error}
          </p>
        )}
        <div className="add-move-actions">
          <button type="button" className="danger" disabled={busy} onClick={onConfirm}>
            {busy ? 'Deleting…' : 'Delete repertoire'}
          </button>
          <button
            ref={cancelRef}
            type="button"
            className="secondary"
            disabled={busy}
            onClick={onCancel}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export function RepertoireList(): JSX.Element {
  const api = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [side, setSide] = useState('all');
  const [order, setOrder] = useState('recent');
  const [toDelete, setToDelete] = useState<Repertoire | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [creating, setCreating] = useState<'white' | 'black' | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['repertoires'],
    queryFn: () => api.listRepertoires(),
  });

  async function onDelete(): Promise<void> {
    if (toDelete === null) return;
    setDeleteError(null);
    setDeleting(true);
    try {
      const { etag } = await api.getRepertoire(toDelete.repertoireId);
      if (etag === null) {
        throw new Error('Could not load the current repertoire version. Refresh and try again.');
      }
      await api.deleteRepertoire(toDelete.repertoireId, etag);
      setToDelete(null);
      await queryClient.invalidateQueries({ queryKey: ['repertoires'] });
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : 'Failed to delete the repertoire.');
    } finally {
      setDeleting(false);
    }
  }

  async function start(sideToPlay: 'white' | 'black'): Promise<void> {
    if (creating !== null) return;
    setCreating(sideToPlay);
    setCreateError(null);
    try {
      const existingCount = (data?.items ?? []).filter((item) => item.side === sideToPlay).length;
      const name = `My ${sideToPlay === 'white' ? 'White' : 'Black'} repertoire${existingCount > 0 ? ` ${existingCount + 1}` : ''}`;
      const { data: created } = await api.createRepertoire(
        { name, side: sideToPlay, visibility: 'private', rootFen: STARTING_FEN, settings: {} },
        crypto.randomUUID(),
      );
      await queryClient.invalidateQueries({ queryKey: ['repertoires'] });
      navigate(buildNodeRoute({
        repertoireId: created.repertoireId,
        zobristHash: created.defaultRoot.position.zobristHash,
        positionId: created.defaultRoot.position.positionId,
        path: null,
      }));
    } catch (cause) {
      setCreateError(cause instanceof Error ? cause.message : 'Could not start a repertoire. Try again.');
    } finally {
      setCreating(null);
    }
  }

  const visible = (data?.items ?? [])
    .filter(
      (r) =>
        r.name.toLowerCase().includes(search.toLowerCase()) && (side === 'all' || r.side === side),
    )
    .sort((a, b) =>
      order === 'name' ? a.name.localeCompare(b.name) : b.updatedAt.localeCompare(a.updatedAt),
    );
  const latest = [...(data?.items ?? [])].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0];
  return (
    <main className="page library-page">
      <section className="builder-start" aria-labelledby="builder-start-title">
        <div className="builder-start-main">
          <p className="studio-eyebrow">YOUR NEXT OPENING</p>
          <h1 id="builder-start-title">Start on the board</h1>
          <p>Choose your side. Play a move. Your repertoire saves as you go.</p>
          {latest && (
            <Link className="builder-continue" to={recentBuilderRoute(latest.repertoireId, buildNodeRoute({
              repertoireId: latest.repertoireId,
              zobristHash: latest.defaultRoot.position.zobristHash,
              positionId: latest.defaultRoot.position.positionId,
              path: null,
            }))}>
              Continue building {latest.name} →
            </Link>
          )}
          <div className="builder-side-actions">
            <button type="button" disabled={creating !== null} onClick={() => void start('white')}>
              {creating === 'white' ? 'Starting…' : 'Start with White'}
            </button>
            <button type="button" disabled={creating !== null} onClick={() => void start('black')}>
              {creating === 'black' ? 'Starting…' : 'Start with Black'}
            </button>
          </div>
          {createError && <p role="alert" className="error">{createError}</p>}
        </div>
        <GameLinkPrompt />
      </section>
      <header className="page-heading library-heading">
        <p className="studio-eyebrow">YOUR OPENING COLLECTIONS</p>
        <h2>Your repertoires</h2>
      </header>
      <div className="library-toolbar">
        <label className="library-search">
          <Search size={17} />
          <span className="sr-only">Search repertoires</span>
          <input
            type="search"
            placeholder="Find your next study…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        <select aria-label="Filter by side" value={side} onChange={(e) => setSide(e.target.value)}>
          <option value="all">All sides</option>
          <option value="white">White</option>
          <option value="black">Black</option>
          <option value="both">Both sides</option>
        </select>
        <select
          aria-label="Sort repertoires"
          value={order}
          onChange={(e) => setOrder(e.target.value)}
        >
          <option value="recent">Recently updated</option>
          <option value="name">Name A–Z</option>
        </select>
      </div>
      <p className="library-count">
        {visible.length} repertoire{visible.length === 1 ? '' : 's'}
      </p>
      {isLoading && <p role="status">Loading repertoires…</p>}
      {isError && (
        <p role="alert">
          Could not load your library. <button onClick={() => void refetch()}>Try again</button>
        </p>
      )}

      {data && data.items.length === 0 && (
        <p role="status" className="muted">
          No repertoires yet. Choose a side above to start playing moves.
        </p>
      )}
      {data && data.items.length > 0 && visible.length === 0 && (
        <div className="library-empty">
          <Search size={28} />
          <h2>No matching repertoires</h2>
          <p>Try another name or reset your filters.</p>
          <button
            onClick={() => {
              setSearch('');
              setSide('all');
            }}
          >
            Reset filters
          </button>
        </div>
      )}
      {data && (
        <ul className="repertoire-list library-grid">
          {visible.map((repertoire) => (
            <li key={repertoire.repertoireId} className="repertoire-row library-card">
              <div className="library-card-top">
                <BookOpen size={22} />
                <span className="side-tag">
                  {repertoire.side === 'both' ? 'Both sides' : `${repertoire.side} repertoire`}
                </span>
              </div>
              <Link
                to={recentBuilderRoute(repertoire.repertoireId, buildNodeRoute({
                  repertoireId: repertoire.repertoireId,
                  zobristHash: repertoire.defaultRoot.position.zobristHash,
                  positionId: repertoire.defaultRoot.position.positionId,
                  path: null,
                }))}
              >
                {repertoire.name} <ArrowUpRight size={18} />
              </Link>
              <Link className="manage-lines" to={`/repertoires/${repertoire.repertoireId}`}>
                Manage lines
              </Link>
              <DueBadge repertoireId={repertoire.repertoireId} />
              <p className="library-updated">
                Updated{' '}
                {new Date(repertoire.updatedAt).toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </p>
              <button
                type="button"
                className="row-delete"
                onClick={() => {
                  setDeleteError(null);
                  setToDelete(repertoire);
                }}
              >
                Delete
              </button>
            </li>
          ))}
        </ul>
      )}
      {toDelete !== null && (
        <ConfirmDeleteModal
          repertoire={toDelete}
          busy={deleting}
          error={deleteError}
          onConfirm={() => void onDelete()}
          onCancel={() => {
            setToDelete(null);
            setDeleteError(null);
          }}
        />
      )}
    </main>
  );
}
